import React from 'react';
import { HistoryItem } from '../types';
import { X, Clock, ChevronRight, Trash2 } from 'lucide-react';

interface HistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  history: HistoryItem[];
  onSelect: (item: HistoryItem) => void;
  onClear: () => void;
}

const HistorySidebar: React.FC<HistorySidebarProps> = ({ 
  isOpen, 
  onClose, 
  history, 
  onSelect,
  onClear 
}) => {
  
  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  };

  const getScoreColor = (score: number) => {
    if (score < 50) return 'text-red-400 bg-red-950/30 border-red-900/50';
    if (score < 80) return 'text-yellow-400 bg-yellow-950/30 border-yellow-900/50';
    return 'text-emerald-400 bg-emerald-950/30 border-emerald-900/50';
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-full md:w-96 bg-zinc-900/95 border-l border-zinc-800 z-50 transform transition-transform duration-300 ease-out shadow-2xl flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800 bg-black/20">
          <div>
             <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-500" />
                Historial
             </h2>
             <p className="text-xs text-zinc-500 mt-1">
                {history.length} análisis guardados
             </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
          {history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-zinc-500">
                <Clock className="w-10 h-10 mb-3 opacity-20" />
                <p>No hay historial reciente.</p>
            </div>
          ) : (
            history.map((item) => (
              <button
                key={item.id}
                onClick={() => onSelect(item)}
                className="w-full text-left group relative overflow-hidden bg-black/40 hover:bg-zinc-800/50 border border-zinc-800/50 hover:border-cyan-900/50 rounded-xl p-4 transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                    <span className="text-xs font-mono text-zinc-500 flex items-center gap-1">
                        {formatDate(item.timestamp)}
                    </span>
                    <div className={`px-2 py-0.5 rounded text-xs font-bold border ${getScoreColor(item.analysisResult.score)}`}>
                        {item.analysisResult.score}/100
                    </div>
                </div>
                
                <h4 className="text-sm font-medium text-gray-300 line-clamp-2 mb-2 group-hover:text-white transition-colors">
                    {item.originalPrompt}
                </h4>
                
                <div className="flex items-center text-xs text-cyan-500/70 opacity-0 group-hover:opacity-100 transition-opacity gap-1">
                    <span>Ver resultado</span>
                    <ChevronRight className="w-3 h-3" />
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        {history.length > 0 && (
            <div className="p-4 border-t border-zinc-800 bg-black/20">
                <button 
                    onClick={onClear}
                    className="w-full flex items-center justify-center gap-2 text-sm text-red-400/70 hover:text-red-400 hover:bg-red-950/20 py-3 rounded-xl transition-all"
                >
                    <Trash2 className="w-4 h-4" />
                    Borrar Historial
                </button>
            </div>
        )}
      </div>
    </>
  );
};

export default HistorySidebar;